(ns io.daskul.webapp.widgets.dashboard.dates
  "Pure date helpers for the dashboard's range selector — no React/re-frame
   deps, so they run under the :node-test unit build (same reason as
   widgets.dashboard.layout).

   `format-date` was defined identically in both `events` and `date-range`;
   it lives here once now. `parse-date` is its inverse, and exists because the
   applied range comes back from app-db as the YYYY-MM-DD strings the API was
   given, while the picker holds Dates (#749)."
  (:require
   [clojure.string :as str]))

(defn format-date
  "Format a JS Date as YYYY-MM-DD. nil-safe."
  [d]
  (when d
    (let [year  (.getFullYear d)
          month (str (inc (.getMonth d)))
          day   (str (.getDate d))]
      (str year "-" (.padStart month 2 "0") "-" (.padStart day 2 "0")))))

(defn parse-date
  "Parse a YYYY-MM-DD string into a LOCAL JS Date. nil-safe; nil on garbage.

   Deliberately not `(js/Date. s)`: that parses a bare date string as UTC
   midnight, which anywhere west of Greenwich renders as the PREVIOUS day —
   the picker would show June 30 for a range starting July 1. Constructing
   from parts gives local midnight, matching what `format-date` produced."
  [s]
  (when-not (str/blank? s)
    (let [[y m d] (map js/parseInt (str/split s #"-"))]
      (when (and y m d (not (js/isNaN y)) (not (js/isNaN m)) (not (js/isNaN d)))
        (js/Date. y (dec m) d)))))

(def ^:private month-names
  ["January" "February" "March" "April" "May" "June"
   "July" "August" "September" "October" "November" "December"])

(defn human-range
  "Human label for an applied [start end] range, e.g. \"1–31 August 2026\" (same
   month), \"28 Jul – 5 Aug 2026\" (same year), or \"28 Dec 2025 – 5 Jan 2026\"
   (cross-year). nil when either end is missing. Takes YYYY-MM-DD strings."
  [start-str end-str]
  (let [s (parse-date start-str) e (parse-date end-str)]
    (when (and s e)
      (let [sy (.getFullYear s) ey (.getFullYear e)
            sm (.getMonth s)     em (.getMonth e)
            sd (.getDate s)      ed (.getDate e)
            full (fn [m] (nth month-names m))
            abbr (fn [m] (subs (nth month-names m) 0 3))]
        (cond
          (and (= sy ey) (= sm em)) (str sd "–" ed " " (full sm) " " sy)
          (= sy ey)                 (str sd " " (abbr sm) " – " ed " " (abbr em) " " sy)
          :else                     (str sd " " (abbr sm) " " sy " – " ed " " (abbr em) " " ey))))))

(defn preset-range
  "{:start-date :end-date} (YYYY-MM-DD strings) for a selector preset key. The
   single home for this — the fetch events and the header's date label both read
   it, so the applied range and the label it shows can never drift."
  [preset]
  (let [today (js/Date.)
        year  (.getFullYear today)
        month (.getMonth today)
        monday-offset (let [d (.getDay today)] (if (zero? d) 6 (dec d)))
        monday (doto (js/Date. (.getTime today))
                 (.setDate (- (.getDate today) monday-offset)))]
    (case preset
      "this-week"    {:start-date (format-date monday)
                      :end-date   (format-date today)}
      "this-month"   {:start-date (format-date (js/Date. year month 1))
                      :end-date   (format-date today)}
      "last-30-days" {:start-date (format-date (doto (js/Date. (.getTime today))
                                                 (.setDate (- (.getDate today) 30))))
                      :end-date   (format-date today)}
      "last-90-days" {:start-date (format-date (doto (js/Date. (.getTime today))
                                                 (.setDate (- (.getDate today) 90))))
                      :end-date   (format-date today)}
      "this-year"    {:start-date (format-date (js/Date. year 0 1))
                      :end-date   (format-date today)}
      {:start-date (format-date (js/Date. year month 1))
       :end-date   (format-date today)})))
